import React, { useContext, useState, useEffect } from "react";
import { DataContext } from "../contexts/DataContext";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import GlobalHeader from "./GlobalHeader";
import AnimatedKPICard from "./AnimatedKPICard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilter,
  faSpinner,
  faFileExcel,
  faCalendarAlt,
  faTimes,
  faChevronDown,
  faChevronUp,
  faChartBar,
  faChartLine,
  faChartPie,
  faTable,
  faSearch,
  faSync,
  faInfoCircle,
  faShip,
  faTruck,
  faPlane,
  faQuestion,
  faTag,
  faBuilding,
  faMapMarkerAlt,
  faIdCard,
  faCog,
  faFileInvoice,
  faClipboardCheck,
  faColumns,
  faExpand,
  faCompress,
  faHome,
  faLayerGroup,
  faCubes,
  faDownload,
  faArrowRight,
  faProcedures,
  faQuestionCircle,
  faBell,
  faEnvelope,
  faUser,
  faSignOutAlt,
  faGlobe,
  faClock,
  faCheckCircle,
  faExclamationTriangle,
  faUsers,
  faBoxes,
  faDollarSign,
  faPercentage,
  faEye,
  faMapMarkedAlt,
} from "@fortawesome/free-solid-svg-icons";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import Select from "react-select";

// Import chart components
import ReferencesPerEntryPointBarChart from "./ReferencesPerEntryPointBarChart";
import EnhancedReferencesPerDocumentTypeBarChart from "./EnhancedReferencesPerDocumentTypeBarChart";
import EnhancedReferencesPerStatusBarChart from "./EnhancedReferencesPerStatusBarChart";
import EnhancedReferencesPerOperationTypeDonutChart from "./EnhancedReferencesPerOperationTypeDonutChart";
import EnhancedReferencesPerEntryPointAndLicenseBarChart from "./EnhancedReferencesPerEntryPointAndLicenseBarChart";
import ReferencesDistributionByCarrierBarChart from "./ReferencesDistributionByCarrierBarChart";
import EnhancedAttentionTimeBarChart from "./EnhancedAttentionTimeBarChart";
import DocTimeBarChart from "./DocTimeBarChart";
import EnhancedAttentionDocumentTimesSeriesChart from "./EnhancedAttentionDocumentTimesSeriesChart";
import EnhancedReferencesPerEntryPointBarChart from "./EnhancedReferencesPerEntryPointBarChart";
import ProcessingEfficiencyMatrixChart from "./ProcessingEfficiencyMatrixChart";
import ProcessBottleneckAnalyzer from "./ProcessBottleneckAnalyzer";
import DrillDownModal from "./DrillDownModal";

const CusmxControlTowerDashboardEnhanced = () => {
  const { trafficData, isLoading, fetchTrafficData, setShouldFetchData } = useContext(DataContext);
  const { logout } = useAuth();
  const navigate = useNavigate();

  // State for managing filtered data
  const [filteredData, setFilteredData] = useState([]);

  // State for layout and UI control
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedChart, setSelectedChart] = useState(null);
  const [detailsItem, setDetailsItem] = useState(null);
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  const [expandedView, setExpandedView] = useState(false);

  // State for drill-down modal
  const [showDrillDownModal, setShowDrillDownModal] = useState(false);
  const [drillDownConfig, setDrillDownConfig] = useState(null);

  // State for filters
  const [showFilters, setShowFilters] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);

  // State for menu dropdowns
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [dateRange, setDateRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: "selection",
    },
  ]);

  // Filter states
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [selectedDivisions, setSelectedDivisions] = useState([]);
  const [selectedRfcs, setSelectedRfcs] = useState([]);
  const [selectedOperationTypes, setSelectedOperationTypes] = useState([]);
  const [selectedEntryPoints, setSelectedEntryPoints] = useState([]);
  const [selectedPedimentos, setSelectedPedimentos] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Filter options
  const [customerOptions, setCustomerOptions] = useState([]);
  const [divisionOptions, setDivisionOptions] = useState([]);
  const [rfcOptions, setRfcOptions] = useState([]);
  const [operationTypeOptions, setOperationTypeOptions] = useState([]);
  const [entryPointOptions, setEntryPointOptions] = useState([]);
  const [pedimentoOptions, setPedimentoOptions] = useState([]);
  const statusOptions = [
    { value: "Paid", label: "Paid" },
    { value: "Open", label: "Open" },
  ];

  // Grid and pagination state
  const [sortConfig, setSortConfig] = useState({
    key: "detail.dates_data.date_init",
    direction: "desc",
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 1,
    totalItems: 0,
  });

  // Chart display configuration
  const [chartLayout, setChartLayout] = useState({
    columns: 2,
    size: "medium",
  });

  // State for KPI data
  const [kpiData, setKpiData] = useState({
    totalSessions: 0,
    totalUsers: 0,
    totalRevenue: 0,
    processingEfficiency: 0,
    activeOperations: 0,
    avgProcessingTime: 0,
    completedToday: 0,
    pendingOperations: 0,
  });

  // Define available charts
  const availableCharts = [
    {
      id: "entryPoint",
      title: "Traffic by Entry Point",
      component: EnhancedReferencesPerEntryPointBarChart,
      icon: faShip,
      size: "large",
    },
    {
      id: "documentType",
      title: "Document Types Distribution",
      component: EnhancedReferencesPerDocumentTypeBarChart,
      icon: faFileInvoice,
      size: "medium",
    },
    {
      id: "status",
      title: "Status Overview",
      component: EnhancedReferencesPerStatusBarChart,
      icon: faTag,
      size: "medium",
    },
    {
      id: "operationType",
      title: "Operation Types",
      component: EnhancedReferencesPerOperationTypeDonutChart,
      icon: faCog,
      size: "medium",
    },
    {
      id: "entryPointLicense",
      title: "Entry Point & License Analysis",
      component: EnhancedReferencesPerEntryPointAndLicenseBarChart,
      icon: faIdCard,
      size: "large",
    },
    {
      id: "carrier",
      title: "Carrier Distribution",
      component: ReferencesDistributionByCarrierBarChart,
      icon: faTruck,
      size: "medium",
    },
    {
      id: "attentionTime",
      title: "Processing Time Analysis",
      component: EnhancedAttentionTimeBarChart,
      icon: faChartBar,
      size: "medium",
    },
    {
      id: "docTime",
      title: "Document Processing Time",
      component: DocTimeBarChart,
      icon: faChartLine,
      size: "medium",
    },
    {
      id: "timeSeries",
      title: "Traffic Trends Over Time",
      component: EnhancedAttentionDocumentTimesSeriesChart,
      icon: faChartLine,
      size: "large",
    },
    {
      id: "processingEfficiency",
      title: "Processing Efficiency Matrix",
      component: ProcessingEfficiencyMatrixChart,
      icon: faProcedures,
      size: "large",
    },
    {
      id: "processBottleneckAnalyzer",
      title: "Bottleneck Analysis",
      component: ProcessBottleneckAnalyzer,
      icon: faProcedures,
      size: "large",
    },
  ];

  // Selected charts for dashboard display
  const [selectedCharts, setSelectedCharts] = useState([
    "entryPoint",
    "documentType",
    "status",
    "operationType",
    "entryPointLicense",
    "attentionTime",
    "timeSeries",
    "processingEfficiency",
    "processBottleneckAnalyzer",
  ]);

  // Fetch data when component mounts
  useEffect(() => {
    fetchTrafficData();
  }, [fetchTrafficData]);

  // Process traffic data to extract filter options and calculate KPIs
  useEffect(() => {
    if (trafficData && trafficData.length > 0) {
      // Extract filter options from data
      const uniqueCustomers = Array.from(
        new Set(trafficData.map((item) => item.header.customer_name))
      )
        .filter(Boolean)
        .map((customer) => ({ value: customer, label: customer }));
      setCustomerOptions(uniqueCustomers);

      const uniqueDivisions = Array.from(
        new Set(trafficData.map((item) => item.header.customer_division))
      )
        .filter(Boolean)
        .map((division) => ({ value: division, label: division }));
      setDivisionOptions(uniqueDivisions);

      const uniqueRfcs = Array.from(
        new Set(trafficData.map((item) => item.header.customer_rfc))
      )
        .filter(Boolean)
        .map((rfc) => ({ value: rfc, label: rfc }));
      setRfcOptions(uniqueRfcs);

      const uniqueOperationTypes = Array.from(
        new Set(trafficData.map((item) => item.header.operation_type))
      )
        .filter(Boolean)
        .map((operationType) => ({
          value: operationType,
          label: getOperationTypeText(operationType),
        }));
      setOperationTypeOptions(uniqueOperationTypes);

      const uniqueEntryPoints = Array.from(
        new Set(
          trafficData
            .map((item) => item.header.pedimento?.pedimento_entry_point)
            .filter(Boolean)
        )
      ).map((entryPoint) => ({ value: entryPoint, label: entryPoint }));
      setEntryPointOptions(uniqueEntryPoints);

      const uniquePedimentos = Array.from(
        new Set(
          trafficData
            .map((item) => item.header.pedimento?.pedimento_number)
            .filter(Boolean)
        )
      ).map((pedimento) => ({ value: pedimento, label: pedimento }));
      setPedimentoOptions(uniquePedimentos);

      // Calculate KPI data
      calculateKPIs(trafficData);

      // Set filtered data to all data initially
      setFilteredData(trafficData);
    }
  }, [trafficData]);

  // Calculate KPI metrics
  const calculateKPIs = (data) => {
    const totalSessions = data.length;
    const totalUsers = new Set(data.map(item => item.header.customer_rfc)).size;

    // Calculate mock revenue based on shipment count and avg value
    const totalRevenue = data.length * 2500; // Mock average value per shipment

    // Calculate processing efficiency (percentage of paid vs total)
    const paidItems = data.filter(item =>
      item.header.detail?.traffic_data?.status === "Pagado"
    ).length;
    const processingEfficiency = totalSessions > 0 ? (paidItems / totalSessions) * 100 : 0;

    // Count active operations (unpaid items)
    const activeOperations = totalSessions - paidItems;

    // Calculate average processing time (mock calculation)
    const avgProcessingTime = 4.2; // Mock average in days

    // Today's completed items (mock - last 24h worth)
    const completedToday = Math.floor(totalSessions * 0.1);

    const pendingOperations = activeOperations;

    setKpiData({
      totalSessions,
      totalUsers,
      totalRevenue,
      processingEfficiency,
      activeOperations,
      avgProcessingTime,
      completedToday,
      pendingOperations,
    });
  };

  // Apply filters to data
  useEffect(() => {
    if (!trafficData) return;

    let filtered = trafficData;

    // Apply search term filter
    if (searchTerm) {
      filtered = filtered.filter((item) => {
        const checkObjectForMatch = (obj, term) => {
          if (!obj) return false;

          for (const key in obj) {
            const value = obj[key];

            if (value && typeof value === "object" && !Array.isArray(value)) {
              if (checkObjectForMatch(value, term)) {
                return true;
              }
            } else if (String(value).toLowerCase().includes(term)) {
              return true;
            }
          }

          return false;
        };

        return checkObjectForMatch(item.header, searchTerm.toLowerCase());
      });
    }

    // Apply dropdown filters
    if (selectedCustomers.length) {
      filtered = filtered.filter((item) =>
        selectedCustomers.some(
          (selected) => selected.value === item.header.customer_name
        )
      );
    }

    if (selectedDivisions.length) {
      filtered = filtered.filter((item) =>
        selectedDivisions.some(
          (selected) => selected.value === item.header.customer_division
        )
      );
    }

    if (selectedRfcs.length) {
      filtered = filtered.filter((item) =>
        selectedRfcs.some(
          (selected) => selected.value === item.header.customer_rfc
        )
      );
    }

    if (selectedOperationTypes.length) {
      filtered = filtered.filter((item) =>
        selectedOperationTypes.some(
          (selected) => selected.value === item.header.operation_type
        )
      );
    }

    if (selectedEntryPoints.length) {
      filtered = filtered.filter((item) =>
        selectedEntryPoints.some(
          (selected) =>
            selected.value === item.header.pedimento.pedimento_entry_point
        )
      );
    }

    if (selectedPedimentos.length) {
      filtered = filtered.filter((item) =>
        selectedPedimentos.some(
          (selected) =>
            selected.value === item.header.pedimento.pedimento_number
        )
      );
    }

    if (selectedStatus.length) {
      filtered = filtered.filter((item) => {
        const status =
          item.header.detail?.traffic_data?.status === "Pagado"
            ? "Paid"
            : "Open";
        return selectedStatus.some((selected) => selected.value === status);
      });
    }

    // Apply date range filter
    const { startDate, endDate } = dateRange[0];
    if (startDate && endDate) {
      filtered = filtered.filter((item) => {
        const dateInit = item.header.detail?.dates_data?.date_init
          ? new Date(item.header.detail.dates_data.date_init)
          : null;
        return dateInit && dateInit >= startDate && dateInit <= endDate;
      });
    }

    setFilteredData(filtered);
    calculateKPIs(filtered);

    // Update pagination
    const totalPages = Math.ceil(filtered.length / pagination.pageSize);
    setPagination((prev) => ({
      ...prev,
      totalPages: totalPages,
      totalItems: filtered.length,
      currentPage: 1,
    }));
  }, [
    trafficData,
    searchTerm,
    selectedCustomers,
    selectedDivisions,
    selectedRfcs,
    selectedOperationTypes,
    selectedEntryPoints,
    selectedPedimentos,
    selectedStatus,
    dateRange,
    pagination.pageSize,
  ]);

  // Trigger data fetch when component mounts
  useEffect(() => {
    setShouldFetchData((prev) => {
      const prevObject = typeof prev === "object" ? prev : {};
      return {
        ...prevObject,
        fetchTrafficData: true,
      };
    });

    return () => {
      setShouldFetchData((prev) => {
        const prevObject = typeof prev === "object" ? prev : {};
        return {
          ...prevObject,
          fetchTrafficData: false,
        };
      });
    };
  }, [setShouldFetchData]);

  const handleRefreshData = () => {
    setShouldFetchData((prev) => ({
      ...prev,
      fetchTrafficData: true,
    }));
    fetchTrafficData();
  };

  // Handle logout functionality
  const handleLogout = () => {
    logout();
    navigate("/");
  };

  // Helper functions
  const getOperationTypeText = (operationType) => {
    if (operationType === "1") return "Import";
    if (operationType === "2") return "Export";
    return operationType;
  };

  const getTransportIcon = (entryPoint) => {
    if (!entryPoint)
      return {
        icon: faQuestion,
        color: "text-gray-500",
        title: "Unknown Transport Type",
      };

    const transportIconsMap = {
      // Seafreight
      160: { icon: faShip, color: "text-blue-600", title: "Sea Freight" },
      510: { icon: faShip, color: "text-blue-600", title: "Sea Freight" },
      430: { icon: faShip, color: "text-blue-600", title: "Sea Freight" },

      // Roadfreight
      240: { icon: faTruck, color: "text-green-600", title: "Road Freight" },

      // Airfreight
      520: { icon: faPlane, color: "text-purple-600", title: "Air Freight" },
      521: { icon: faPlane, color: "text-purple-600", title: "Air Freight" },
      640: { icon: faPlane, color: "text-purple-600", title: "Air Freight" },

      // Default for unknown entry points
      default: {
        icon: faQuestion,
        color: "text-gray-500",
        title: "Unknown Transport Type",
      },
    };

    const entryPointNum =
      typeof entryPoint === "string" ? parseInt(entryPoint, 10) : entryPoint;
    return transportIconsMap[entryPointNum] || transportIconsMap["default"];
  };

  const clearAllFilters = () => {
    setSelectedCustomers([]);
    setSelectedDivisions([]);
    setSelectedRfcs([]);
    setSelectedOperationTypes([]);
    setSelectedEntryPoints([]);
    setSelectedPedimentos([]);
    setSelectedStatus([]);
    setSearchTerm("");
    setDateRange([
      {
        startDate: null,
        endDate: null,
        key: "selection",
      },
    ]);
  };

  const clearFilterType = (filterType) => {
    switch (filterType) {
      case "customers":
        setSelectedCustomers([]);
        break;
      case "divisions":
        setSelectedDivisions([]);
        break;
      case "rfcs":
        setSelectedRfcs([]);
        break;
      case "operationTypes":
        setSelectedOperationTypes([]);
        break;
      case "entryPoints":
        setSelectedEntryPoints([]);
        break;
      case "pedimentos":
        setSelectedPedimentos([]);
        break;
      case "status":
        setSelectedStatus([]);
        break;
      case "search":
        setSearchTerm("");
        break;
      case "dateRange":
        setDateRange([
          {
            startDate: null,
            endDate: null,
            key: "selection",
          },
        ]);
        break;
      default:
        break;
    }
  };

  // Format date for display
  const formatDate = (date) => {
    if (!date) return "N/A";
    try {
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) return "N/A";

      const options = { year: "numeric", month: "short", day: "numeric" };
      return dateObj.toLocaleDateString(undefined, options);
    } catch (error) {
      console.error("Error formatting date:", error);
      return "N/A";
    }
  };

  // Get status color class based on status
  const getStatusColorClass = (status) => {
    if (status === "Pagado") return "bg-green-100 text-green-800";
    return "bg-yellow-100 text-yellow-800";
  };

  // Request sort by column
  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key) {
      direction = sortConfig.direction === "asc" ? "desc" : "asc";
    }
    setSortConfig({ key, direction });
  };

  // Check if any filters are active
  const hasActiveFilters =
    selectedCustomers.length > 0 ||
    selectedDivisions.length > 0 ||
    selectedRfcs.length > 0 ||
    selectedOperationTypes.length > 0 ||
    selectedEntryPoints.length > 0 ||
    selectedPedimentos.length > 0 ||
    selectedStatus.length > 0 ||
    (dateRange[0].startDate && dateRange[0].endDate) ||
    searchTerm.length > 0;

  // Handle chart click to show drill-down modal
  const handleChartItemClick = (chartId, itemData) => {
    const config = {
      chartType: itemData.type || "unknown",
      entryPoint: itemData.entryPoint && itemData.entryPoint !== "Unknown" ? itemData.entryPoint : null,
      clickedBracket: itemData.bracketType || null,
      count: itemData.count || 0,
    };

    setDrillDownConfig(config);
    setShowDrillDownModal(true);
  };

  // Handle drill-down modal close
  const handleCloseDrillDownModal = () => {
    setShowDrillDownModal(false);
    setDrillDownConfig(null);
  };

  // Handle page change in pagination
  const handlePageChange = (page) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: page,
    }));
  };

  // Handle page size change
  const handlePageSizeChange = (size) => {
    setPagination((prev) => ({
      ...prev,
      pageSize: size,
      currentPage: 1,
    }));
  };

  // Toggle chart layout
  const toggleChartLayout = () => {
    setChartLayout((prev) => ({
      ...prev,
      columns: prev.columns === 2 ? 3 : 2,
    }));
  };

  // Toggle chart size
  const toggleChartSize = () => {
    const sizes = ["small", "medium", "large"];
    const currentIndex = sizes.indexOf(chartLayout.size);
    const nextIndex = (currentIndex + 1) % sizes.length;

    setChartLayout((prev) => ({
      ...prev,
      size: sizes[nextIndex],
    }));
  };

  // Custom select styles for filters
  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? "#3b82f6" : "#e2e8f0",
      boxShadow: state.isFocused ? "0 0 0 1px #3b82f6" : "none",
      "&:hover": {
        borderColor: "#cbd5e1",
      },
      borderRadius: "0.375rem",
      minHeight: "38px",
      transition: "all 0.2s ease",
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: "#e1effe",
      borderRadius: "0.25rem",
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "#1e40af",
      fontWeight: 500,
      fontSize: "0.8125rem",
      padding: "0.125rem 0.25rem",
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: "#60a5fa",
      ":hover": {
        backgroundColor: "#bfdbfe",
        color: "#1e3a8a",
      },
      borderRadius: "0 0.25rem 0.25rem 0",
    }),
    placeholder: (base) => ({
      ...base,
      color: "#94a3b8",
      fontSize: "0.875rem",
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
      borderRadius: "0.375rem",
      boxShadow:
        "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
      border: "1px solid #e2e8f0",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#3b82f6"
        : state.isFocused
        ? "#e1effe"
        : "white",
      color: state.isSelected ? "white" : "#1e293b",
      fontSize: "0.875rem",
      padding: "0.5rem 0.75rem",
      ":active": {
        backgroundColor: state.isSelected ? "#3b82f6" : "#bfdbfe",
      },
    }),
  };

  // Determine chart size class based on layout settings
  const getChartSizeClass = () => {
    const columnClasses = {
      1: "grid-cols-1",
      2: "grid-cols-1 lg:grid-cols-2",
      3: "grid-cols-1 md:grid-cols-2 xl:grid-cols-3",
    };

    const sizeClasses = {
      small: "h-64",
      medium: "h-80",
      large: "h-96",
    };

    return {
      columns: columnClasses[chartLayout.columns],
      height: sizeClasses[chartLayout.size],
    };
  };

  // Calculate paginated data
  const paginatedData = (() => {
    const sortedData = [...filteredData].sort((a, b) => {
      const getNestedValue = (obj, path) =>
        path
          .split(".")
          .reduce((o, key) => (o && o[key] !== undefined ? o[key] : null), obj);

      let aVal = getNestedValue(a.header, sortConfig.key);
      let bVal = getNestedValue(b.header, sortConfig.key);

      if (sortConfig.key.includes("date") && aVal && bVal) {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }

      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    const startIndex = (pagination.currentPage - 1) * pagination.pageSize;
    const endIndex = startIndex + pagination.pageSize;
    return sortedData.slice(startIndex, endIndex);
  })();

  // Render loading spinner
  if (isLoading && (!trafficData || trafficData.length === 0)) {
    return (
      <div className="flex justify-center items-center h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <FontAwesomeIcon
            icon={faSpinner}
            className="text-6xl font-semibold animate-spin text-blue-600 mb-4"
          />
          <div className="text-2xl font-semibold text-gray-700">Loading Enhanced Dashboard...</div>
          <div className="text-sm text-gray-500 mt-2">Preparing your analytics experience</div>
        </div>
      </div>
    );
  }

  // Get chart size styling
  const chartSizeClasses = getChartSizeClass();

  // Custom action buttons for Enhanced Dashboard
  const actionButtons = (
    <>
      <button
        onClick={handleRefreshData}
        className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 text-white"
        disabled={isLoading}
      >
        <FontAwesomeIcon
          icon={isLoading ? faSpinner : faSync}
          className={isLoading ? "animate-spin" : ""}
        />
        <span>{isLoading ? "Refreshing..." : "Refresh Data"}</span>
      </button>
      <button
        onClick={toggleChartLayout}
        className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 text-white"
      >
        <FontAwesomeIcon icon={faColumns} />
        <span>{`${chartLayout.columns} Columns`}</span>
      </button>
      <button
        onClick={toggleChartSize}
        className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 text-white"
      >
        <FontAwesomeIcon
          icon={chartLayout.size === "large" ? faCompress : faExpand}
        />
        <span>{`${
          chartLayout.size.charAt(0).toUpperCase() +
          chartLayout.size.slice(1)
        } Size`}</span>
      </button>
    </>
  );

  return (
    <div className="bg-gradient-to-br from-gray-50 to-blue-50 min-h-screen flex flex-col">
      {/* GlobalHeader */}
      <GlobalHeader
        title="Enhanced Control Tower Dashboard"
        icon={faChartBar}
        actionButtons={actionButtons}
      />

      {/* Tabs */}
      <div className="bg-gradient-to-r from-[#1A2654] to-blue-700">
        <div className="flex px-6 border-b border-blue-600">
          <button
            className={`px-4 py-2 font-medium text-sm rounded-t-lg transition-colors duration-200 ${
              activeTab === "overview"
                ? "bg-white text-blue-800"
                : "bg-transparent text-white hover:bg-blue-600"
            }`}
            onClick={() => setActiveTab("overview")}
          >
            <FontAwesomeIcon icon={faEye} className="mr-2" />
            Real-Time Overview
          </button>
          <button
            className={`px-4 py-2 font-medium text-sm rounded-t-lg transition-colors duration-200 ${
              activeTab === "analytics"
                ? "bg-white text-blue-800"
                : "bg-transparent text-white hover:bg-blue-600"
            }`}
            onClick={() => setActiveTab("analytics")}
          >
            <FontAwesomeIcon icon={faChartPie} className="mr-2" />
            Analytics
          </button>
          <button
            className={`px-4 py-2 font-medium text-sm rounded-t-lg transition-colors duration-200 ${
              activeTab === "data"
                ? "bg-white text-blue-800"
                : "bg-transparent text-white hover:bg-blue-600"
            }`}
            onClick={() => setActiveTab("data")}
          >
            <FontAwesomeIcon icon={faTable} className="mr-2" />
            Data Grid
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 p-6">
        {/* Search and Filter Bar */}
        <div className="mb-6">
          <div className="bg-white shadow-sm rounded-lg border border-gray-200">
            <div className="p-4">
              <div className="flex flex-col md:flex-row gap-3">
                {/* Search input */}
                <div className="w-full md:w-1/3">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FontAwesomeIcon
                        icon={faSearch}
                        className="text-gray-400"
                      />
                    </div>
                    <input
                      type="text"
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="Search shipments, references, customers..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm("")}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Buttons group */}
                <div className="flex flex-wrap gap-2 justify-end ml-auto">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      showFilters
                        ? "bg-blue-100 text-blue-700 border border-blue-200"
                        : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <FontAwesomeIcon icon={faFilter} />
                    <span>Filters</span>
                    <FontAwesomeIcon
                      icon={showFilters ? faChevronUp : faChevronDown}
                      className="text-xs"
                    />
                  </button>

                  <button
                    onClick={() => setShowDatePicker(!showDatePicker)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      showDatePicker ||
                      (dateRange[0].startDate && dateRange[0].endDate)
                        ? "bg-blue-100 text-blue-700 border border-blue-200"
                        : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <FontAwesomeIcon icon={faCalendarAlt} />
                    <span>Date Range</span>
                    {showDatePicker ? (
                      <FontAwesomeIcon icon={faChevronUp} className="text-xs" />
                    ) : (
                      <FontAwesomeIcon
                        icon={faChevronDown}
                        className="text-xs"
                      />
                    )}
                  </button>

                  <button
                    onClick={clearAllFilters}
                    disabled={!hasActiveFilters}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      hasActiveFilters
                        ? "bg-red-50 text-red-700 border border-red-200 hover:bg-red-100"
                        : "bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed"
                    }`}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                    <span>Clear All Filters</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Date picker dropdown */}
            {showDatePicker && (
              <div className="border-t border-gray-200 bg-gray-50 p-4">
                <div className="flex justify-center">
                  <DateRangePicker
                    ranges={dateRange}
                    onChange={(ranges) => setDateRange([ranges.selection])}
                    showSelectionPreview={true}
                    moveRangeOnFirstSelection={false}
                    months={2}
                    direction="horizontal"
                    className="rounded-lg shadow-lg"
                  />
                </div>
              </div>
            )}

            {/* Filters dropdown */}
            {showFilters && (
              <div className="border-t border-gray-200 bg-gray-50 p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Customer filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Customers
                    </label>
                    <Select
                      isMulti
                      value={selectedCustomers}
                      onChange={setSelectedCustomers}
                      options={customerOptions}
                      className="text-sm"
                      placeholder="Select customers..."
                      styles={customSelectStyles}
                    />
                  </div>

                  {/* Division filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Divisions
                    </label>
                    <Select
                      isMulti
                      value={selectedDivisions}
                      onChange={setSelectedDivisions}
                      options={divisionOptions}
                      className="text-sm"
                      placeholder="Select divisions..."
                      styles={customSelectStyles}
                    />
                  </div>

                  {/* RFC filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      RFC
                    </label>
                    <Select
                      isMulti
                      value={selectedRfcs}
                      onChange={setSelectedRfcs}
                      options={rfcOptions}
                      className="text-sm"
                      placeholder="Select RFCs..."
                      styles={customSelectStyles}
                    />
                  </div>

                  {/* Operation Type filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Operation Type
                    </label>
                    <Select
                      isMulti
                      value={selectedOperationTypes}
                      onChange={setSelectedOperationTypes}
                      options={operationTypeOptions}
                      className="text-sm"
                      placeholder="Select operation types..."
                      styles={customSelectStyles}
                    />
                  </div>

                  {/* Entry Point filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Entry Points
                    </label>
                    <Select
                      isMulti
                      value={selectedEntryPoints}
                      onChange={setSelectedEntryPoints}
                      options={entryPointOptions}
                      className="text-sm"
                      placeholder="Select entry points..."
                      styles={customSelectStyles}
                    />
                  </div>

                  {/* Status filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <Select
                      isMulti
                      value={selectedStatus}
                      onChange={setSelectedStatus}
                      options={statusOptions}
                      className="text-sm"
                      placeholder="Select status..."
                      styles={customSelectStyles}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === "overview" && (
          <div className="space-y-6">
            {/* KPI Cards Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <AnimatedKPICard
                title="Total Sessions"
                value={kpiData.totalSessions}
                previousValue={kpiData.totalSessions * 0.9}
                icon={faUsers}
                color="blue"
                format="number"
              />
              <AnimatedKPICard
                title="Active Users"
                value={kpiData.totalUsers}
                previousValue={kpiData.totalUsers * 0.95}
                icon={faUser}
                color="green"
                format="number"
              />
              <AnimatedKPICard
                title="Revenue"
                value={kpiData.totalRevenue}
                previousValue={kpiData.totalRevenue * 0.92}
                icon={faDollarSign}
                color="purple"
                format="currency"
              />
              <AnimatedKPICard
                title="Processing Efficiency"
                value={kpiData.processingEfficiency}
                previousValue={kpiData.processingEfficiency * 0.98}
                icon={faPercentage}
                color="orange"
                format="percentage"
                suffix="%"
              />
            </div>

            {/* Additional KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <AnimatedKPICard
                title="Active Operations"
                value={kpiData.activeOperations}
                previousValue={kpiData.activeOperations * 1.1}
                icon={faCog}
                color="blue"
                format="number"
              />
              <AnimatedKPICard
                title="Avg Processing Time"
                value={kpiData.avgProcessingTime}
                previousValue={kpiData.avgProcessingTime * 1.05}
                icon={faClock}
                color="green"
                format="decimal"
                suffix=" days"
              />
              <AnimatedKPICard
                title="Completed Today"
                value={kpiData.completedToday}
                previousValue={kpiData.completedToday * 0.8}
                icon={faCheckCircle}
                color="purple"
                format="number"
              />
              <AnimatedKPICard
                title="Pending Operations"
                value={kpiData.pendingOperations}
                previousValue={kpiData.pendingOperations * 1.15}
                icon={faExclamationTriangle}
                color="orange"
                format="number"
              />
            </div>

            {/* Featured Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Entry Point Chart */}
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <FontAwesomeIcon icon={faMapMarkedAlt} className="text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800">Traffic Distribution</h3>
                  </div>
                  <button className="text-gray-400 hover:text-gray-600">
                    <FontAwesomeIcon icon={faExpand} />
                  </button>
                </div>
                <div className="h-80">
                  <EnhancedReferencesPerEntryPointBarChart
                    data={filteredData}
                    isLoading={isLoading}
                    onChartItemClick={handleChartItemClick}
                    height="100%"
                  />
                </div>
              </div>

              {/* Operation Type Chart */}
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <FontAwesomeIcon icon={faChartPie} className="text-purple-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800">Operation Types</h3>
                  </div>
                  <button className="text-gray-400 hover:text-gray-600">
                    <FontAwesomeIcon icon={faExpand} />
                  </button>
                </div>
                <div className="h-80">
                  <EnhancedReferencesPerOperationTypeDonutChart
                    data={filteredData}
                    isLoading={isLoading}
                    onChartItemClick={handleChartItemClick}
                    height="100%"
                  />
                </div>
              </div>
            </div>

            {/* Time Series Chart */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <FontAwesomeIcon icon={faChartLine} className="text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800">Traffic Trends Over Time</h3>
                </div>
                <button className="text-gray-400 hover:text-gray-600">
                  <FontAwesomeIcon icon={faExpand} />
                </button>
              </div>
              <div className="h-96">
                <EnhancedAttentionDocumentTimesSeriesChart
                  data={filteredData}
                  isLoading={isLoading}
                  onChartItemClick={handleChartItemClick}
                  height="100%"
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === "analytics" && (
          <div className="space-y-6">
            {/* Analytics Charts Grid */}
            <div className={`grid gap-6 ${chartSizeClasses.columns}`}>
              {selectedCharts
                .filter(chartId => availableCharts.find(chart => chart.id === chartId))
                .map((chartId) => {
                  const chart = availableCharts.find((c) => c.id === chartId);
                  const ChartComponent = chart.component;

                  return (
                    <div
                      key={chartId}
                      className={`bg-white rounded-xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-shadow duration-300 ${
                        chart.size === "large" ? "lg:col-span-2" : ""
                      }`}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <FontAwesomeIcon icon={chart.icon} className="text-blue-600" />
                          </div>
                          <h3 className="text-lg font-semibold text-gray-800">{chart.title}</h3>
                        </div>
                        <button className="text-gray-400 hover:text-gray-600">
                          <FontAwesomeIcon icon={faExpand} />
                        </button>
                      </div>
                      <div className={chartSizeClasses.height}>
                        <ChartComponent
                          data={filteredData}
                          isLoading={isLoading}
                          onChartItemClick={handleChartItemClick}
                          height="100%"
                        />
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {activeTab === "data" && (
          <div className="bg-white rounded-xl shadow-lg border border-gray-100">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-800">
                  Traffic Data ({filteredData.length} records)
                </h3>
                <div className="flex items-center gap-2">
                  <button className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors">
                    <FontAwesomeIcon icon={faFileExcel} />
                    <span>Export to Excel</span>
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Reference
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Entry Point
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Operation Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedData.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                          {item.header.reference}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.header.customer_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex items-center gap-2">
                            <FontAwesomeIcon
                              icon={getTransportIcon(item.header.pedimento?.pedimento_entry_point).icon}
                              className={getTransportIcon(item.header.pedimento?.pedimento_entry_point).color}
                            />
                            {item.header.pedimento?.pedimento_entry_point}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {getOperationTypeText(item.header.operation_type)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            item.header.detail?.traffic_data?.status === "Pagado"
                              ? "bg-green-100 text-green-800"
                              : "bg-yellow-100 text-yellow-800"
                          }`}>
                            {item.header.detail?.traffic_data?.status === "Pagado" ? "Paid" : "Open"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(item.header.detail?.dates_data?.date_init)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between mt-6">
                  <div className="text-sm text-gray-700">
                    Showing {(pagination.currentPage - 1) * pagination.pageSize + 1} to{" "}
                    {Math.min(pagination.currentPage * pagination.pageSize, pagination.totalItems)} of{" "}
                    {pagination.totalItems} results
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePageChange(pagination.currentPage - 1)}
                      disabled={pagination.currentPage === 1}
                      className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Previous
                    </button>
                    {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                      .filter(page =>
                        page === 1 ||
                        page === pagination.totalPages ||
                        Math.abs(page - pagination.currentPage) <= 2
                      )
                      .map((page, index, arr) => (
                        <React.Fragment key={page}>
                          {index > 0 && arr[index - 1] !== page - 1 && <span>...</span>}
                          <button
                            onClick={() => handlePageChange(page)}
                            className={`px-3 py-1 rounded border text-sm ${
                              pagination.currentPage === page
                                ? "bg-blue-600 text-white border-blue-600"
                                : "border-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            {page}
                          </button>
                        </React.Fragment>
                      ))}
                    <button
                      onClick={() => handlePageChange(pagination.currentPage + 1)}
                      disabled={pagination.currentPage === pagination.totalPages}
                      className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Drill-down Modal */}
      {showDrillDownModal && drillDownConfig && (
        <DrillDownModal
          config={drillDownConfig}
          data={filteredData}
          onClose={handleCloseDrillDownModal}
        />
      )}
    </div>
  );
};

export default CusmxControlTowerDashboardEnhanced;